/**
 * Hues.js — Component System
 * -----------------------------------------------------------------------------
 * Defines `defineComponent`, `createApp`, lifecycle hooks, slots, mixins,
 * provide/inject, and the `currentInstance` context that powers every other
 * Hues.js feature.
 *
 * © Death Legion — Hues.js
 */

import { VNode, Component, ComponentContext, h, render as vdomRender } from './vdom'
import { reactive, ref, computed, isRef } from './reactivity'

export interface ComponentInstance {
  type: Component
  props: Record<string, any>
  attrs: Record<string, any>
  slots: Record<string, () => VNode[]>
  state: Record<string, any>
  ctx: ComponentContext
  vnode: VNode
  subTree: VNode | null
  isMounted: boolean
  effects: Array<{ stop?: () => void }>
  exposed: Record<string, any> | null
  provides: Record<string, any>
  render?: () => VNode
  [hookKey: string]: any
}

export const currentInstance: { value: ComponentInstance | null } = { value: null }

export function setCurrentInstance(instance: ComponentInstance | null): void {
  currentInstance.value = instance
}

export function getCurrentInstance(): ComponentInstance | null {
  return currentInstance.value
}

// ----- Lifecycle hook storage -------------------------------------------------

type HookName =
  | 'beforeCreate'
  | 'created'
  | 'beforeMount'
  | 'mounted'
  | 'beforeUpdate'
  | 'updated'
  | 'beforeUnmount'
  | 'unmounted'
  | 'errorCaptured'
  | 'activated'
  | 'deactivated'

export function invokeHook(instance: ComponentInstance, hook: HookName): void {
  const fromComponent = (instance.type as any)[hook]
  if (typeof fromComponent === 'function') {
    try {
      setCurrentInstance(instance)
      fromComponent.call(instance.state || instance.props)
    } finally {
      setCurrentInstance(null)
    }
  }
  const fromInstance = (instance as any)[hook]
  if (Array.isArray(fromInstance)) {
    for (const fn of fromInstance) {
      try {
        setCurrentInstance(instance)
        fn.call(instance.state || instance.props)
      } finally {
        setCurrentInstance(null)
      }
    }
  }
}

function registerHook(name: HookName, fn: (...args: any[]) => any): void {
  const instance = currentInstance.value
  if (!instance) {
    console.warn(`[Hues warn] ${name} called outside of setup().`)
    return
  }
  const existing = (instance as any)[name] as Array<(arg: any) => any> | undefined
  if (existing) existing.push(fn)
  else (instance as any)[name] = [fn]
}

// ----- Public lifecycle APIs --------------------------------------------------

export function onBeforeMount(fn: () => any): void { registerHook('beforeMount', fn) }
export function onMounted(fn: () => any): void { registerHook('mounted', fn) }
export function onBeforeUpdate(fn: () => any): void { registerHook('beforeUpdate', fn) }
export function onUpdated(fn: () => any): void { registerHook('updated', fn) }
export function onBeforeUnmount(fn: () => any): void { registerHook('beforeUnmount', fn) }
export function onUnmounted(fn: () => any): void { registerHook('unmounted', fn) }
export function onErrorCaptured(fn: (err: unknown) => any): void { registerHook('errorCaptured', fn) }
export function onActivated(fn: () => any): void { registerHook('activated', fn) }
export function onDeactivated(fn: () => any): void { registerHook('deactivated', fn) }

// ----- defineComponent --------------------------------------------------------

export function defineComponent(options: any): Component {
  const comp: Component = function (props = {}, ctx) {
    // If no setup function returns a render, fall back to a render fn that
    // composes template/JSX — here we just return an empty fragment if missing.
    return h('div', null, '')
  } as Component

  // Copy safe properties one by one — Function.name is read-only and would
  // throw if we tried to Object.assign({ name }) onto a function.
  if (options.name) {
    try { (comp as any).displayName = options.name } catch { /* noop */ }
  }
  if (options.props) comp.props = options.props
  if (options.emits) comp.emits = options.emits
  if (options.setup) comp.setup = options.setup
  if (options.template) comp.template = options.template
  if (options.render) {
    comp.setup = (props, ctx) => {
      const setupResult = options.setup ? options.setup(props, ctx) : {}
      const render = options.render
      return { ...(setupResult || {}), __render: render }
    }
    const originalSetup = comp.setup!
    comp.setup = (props, ctx) => {
      const result = originalSetup(props, ctx)
      const instance = getCurrentInstance()
      if (instance) {
        instance.render = () => options.render.call({ ...result, ...props, $emit: ctx.emit, $slots: ctx.slots }, h)
      }
      return result
    }
  }
  return comp
}

// ----- createApp --------------------------------------------------------------

export interface App {
  use(plugin: (app: App, ...opts: any[]) => void, ...opts: any[]): App
  provide(key: string | symbol, value: any): App
  component(name: string, comp?: Component): App | Component
  directive(name: string, dir?: any): App | any
  mount(container: string | HTMLElement): ComponentInstance
  unmount(): void
  config: {
    globalProperties: Record<string, any>
    errorHandler?: (err: unknown) => void
  }
  _container?: HTMLElement
  _instance?: ComponentInstance
  _provides: Record<string, any>
  _components: Record<string, Component>
  _directives: Record<string, any>
  _mixins: any[]
}

export function createApp(rootComponent: Component, rootProps?: Record<string, any>): App {
  const app: App = {
    _provides: {},
    _components: {},
    _directives: {},
    _mixins: [],
    config: {
      globalProperties: {},
    },
    use(plugin, ...opts) {
      if (typeof plugin === 'function') {
        plugin(app, ...opts)
      } else if (plugin && typeof (plugin as any).install === 'function') {
        ;(plugin as any).install(app, ...opts)
      } else {
        console.warn('[Hues warn] app.use() expected a function or an object with an install() method.')
      }
      return app
    },
    provide(key, value) {
      app._provides[key] = value
      return app
    },
    component(name, comp) {
      if (comp) {
        app._components[name] = comp
        return app
      }
      return app._components[name]
    },
    directive(name, dir) {
      if (dir) {
        app._directives[name] = dir
        return app
      }
      return app._directives[name]
    },
    mount(container) {
      const el = typeof container === 'string' ? document.querySelector(container) as HTMLElement : container
      if (!el) throw new Error(`[Hues error] Mount target "${container}" not found.`)
      const rootVNode = h(rootComponent, rootProps || {})
      // Inject app-level provides
      const instance: ComponentInstance = {
        type: rootComponent,
        props: rootProps || {},
        attrs: {},
        slots: {},
        state: {},
        ctx: {
          attrs: {},
          slots: {},
          emit: () => {},
          expose: () => {},
        },
        vnode: rootVNode,
        subTree: null,
        isMounted: false,
        effects: [],
        exposed: null,
        provides: { ...app._provides },
      }
      rootVNode.component = instance
      // Render via the vdom module
      vdomRender(rootVNode, el)
      app._container = el
      app._instance = instance
      return instance
    },
    unmount() {
      if (app._container) {
        vdomRender(null, app._container)
      }
    },
  }
  return app
}

// ----- Mixins -----------------------------------------------------------------

export function mixin(app: App, mixinOpts: any): App {
  app._mixins.push(mixinOpts)
  return app
}

// ----- Provide / Inject -------------------------------------------------------

export function provide(key: string | symbol, value: any): void {
  const instance = currentInstance.value
  if (!instance) return
  instance.provides[key] = value
}

export function inject<T>(key: string | symbol, defaultValue?: T): T | undefined {
  const instance = currentInstance.value
  if (!instance) return defaultValue
  if (key in instance.provides) return instance.provides[key as string]
  return defaultValue
}

// ----- Slots helper -----------------------------------------------------------

export function useSlots(): { default?: () => VNode[]; [name: string]: (() => VNode[]) | undefined } {
  const instance = currentInstance.value
  return instance ? instance.ctx.slots : {}
}

// ----- Expose helper ----------------------------------------------------------

export function defineExpose(exposed: Record<string, any>): void {
  const instance = currentInstance.value
  if (!instance) return
  instance.ctx.expose(exposed)
}

// ----- Computed-like composables ----------------------------------------------

export { reactive, ref, computed, isRef }
